import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { isExtensionContextOrWhatsappView } from "@common-services/detect-context";
import { toggleSocketModal } from "@common/reducers/MainReducer";
import { AlertCircle, ChevronDown, ChevronUp, Computer, PlugZap, QrCode, Server, WifiOff } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { navigateToEngines } from '@/common/utils/navigation.util';
import { BrowserExtensionSection } from './components/BrowserExtensionSection';
import { ExpandedView } from './components/ExpandedView';
import { NotifierStatusSection } from './components/NotifierStatusSection';
import { OfflineServerSection } from './components/OfflineServerSection';
import { selectEngineState } from './connected-engines.selectors';
import { engineStatusHint, engineStatusLabel } from './engine-status-copy';
import { useComponentStyles } from './connected-engines.styles';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { useRecordConnectEventMutation } from '@common-reducers/backend-api/backend-api';
import { KeepAwakeControl } from '../whatsapp-tab-throttle-status';
import hs from '../header-status/header-status.module.scss';
import { HeaderHint, type HeaderHintTone } from '../header-status/header-hint';

interface EngineGroup {
  groupKey: string;
  count: number;
  type: string;
  displayName: string;
  domain: string;
  latestConnectionTime: number | string;
}

const EngineList = ({ styles, groupedEngines, t }: { styles: Record<string, React.CSSProperties>; groupedEngines: EngineGroup[]; t: (key: string) => string }) => (
  <div style={styles.engineList}>
    {groupedEngines.map((group: EngineGroup) => {
      const countText = group.count > 1 ? ` (${group.count})` : '';

      return (
        <div key={group.groupKey} style={styles.engineItem}>
          {group.type === 'browser' ? (
            <Computer size={20} color="#f97316" />
          ) : (
            <Server size={20} color="#22c55e" />
          )}
          <div style={styles.engineInfo}>
            <p style={styles.engineName}>{group.displayName}{countText}</p>
            <p style={styles.engineStatus}>{t('status.connectedFrom')}: {group.domain}</p>
            <p style={styles.engineStatus}>
              {t('status.latestConnection')}: {new Date(group.latestConnectionTime).toLocaleString()}
            </p>
          </div>
        </div>
      );
    })}
  </div>
);

const AlertMessage = ({ styles, alert }: { styles: Record<string, React.CSSProperties>; alert: { type: string; message: string } }) => (
  <div
    style={{
      ...styles.alert,
      ...(alert.type === 'error' ? styles.errorAlert : styles.warningAlert)
    }}
  >
    <AlertCircle size={16} />
    <span>{alert.message}</span>
  </div>
);

const ConnectedEngines = () => {
  const { t } = useTranslation(namespace);
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceId = useRef(`connected-engines-${Math.random().toString(36).substr(2, 9)}`).current;
  const styles = useComponentStyles();

  const { engines, groupedEngines, status, hasGateway, isNotifierConnected, session, isInQRMode } = useAppSelector(selectEngineState);

  // First-run = no engine at all and not mid-QR-scan → the amber "Connect WhatsApp" pill.
  const isFirstRun = engines.length === 0 && !isInQRMode;
  const [recordConnectEvent] = useRecordConnectEventMutation();
  const connectImpressionRef = useRef(false);

  // Fire-and-forget activation instrumentation — never block or break the chip. The chip
  // renders in the extension list view too, so it opts into source:'extension' there.
  const fireConnectEvent = (
    name: 'connect_surface_impression' | 'connect_surface_clicked',
    props?: Record<string, unknown>,
  ) => {
    try {
      recordConnectEvent({
        name,
        props: { surface: 'chip', ...(props ?? {}) },
        source: isExtensionContextOrWhatsappView() ? 'extension' : 'web',
      }).catch(() => { /* intentionally ignored */ });
    } catch {
      /* never break the chip */
    }
  };

  // Impression: once per entry into the first-run (amber pill) state.
  useEffect(() => {
    if (!isFirstRun) { connectImpressionRef.current = false; return; }
    if (connectImpressionRef.current) return;
    connectImpressionRef.current = true;
    fireConnectEvent('connect_surface_impression');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstRun]);

  // The reducer still speaks in hex (it predates the header-row tokens), so the
  // chip translates that into the shared tone vocabulary here rather than
  // painting a raw hex onto the new flat chrome. Presentation only — the status
  // itself is untouched.
  const statusToneClass = (color: string): string => {
    if (color === '#22c55e') return hs.toneOk;
    if (color === '#f97316') return hs.toneWarn;
    if (color === '#ef4444') return hs.toneBad;
    return hs.toneNeutral;
  };

  const getTranslatedStatus = (statusText: string) => engineStatusLabel(t, statusText);
  const getStatusHint = (statusText: string) => engineStatusHint(t, statusText);

  const toneOf = (color: string): HeaderHintTone =>
    color === '#22c55e' ? 'ok' : color === '#f97316' ? 'warn' : color === '#ef4444' ? 'bad' : 'neutral';
  const manageFooter = t('status.hint.clickToManage', 'Click to manage your connections');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-action-button]')) {
        return;
      }

      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const handleConnect = () => {
    navigateToEngines();
    setIsExpanded(false);
  };

  const handleAddGateway = () => {
    navigateToEngines();
    setIsExpanded(false);
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFirstRun) fireConnectEvent('connect_surface_clicked', { cta: 'open_connections' });
    navigateToEngines();
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(true);
  };

  return (
    <div style={{ ...styles.container, display: 'flex', alignItems: 'center', gap: '10px' }} ref={containerRef}>
      <KeepAwakeControl />
      {isExpanded && (
        <ExpandedView
          styles={styles}
          onClose={() => setIsExpanded(false)}
          buttonId={instanceId}
          onAddGateway={handleAddGateway}
        >
          <NotifierStatusSection styles={styles} isConnected={isNotifierConnected} />
          {engines.length === 0 ? (
            <>
              <BrowserExtensionSection styles={styles} />
              <div style={styles.divider}>
                <div style={styles.dividerLine} />
                <span style={styles.dividerText}>or</span>
              </div>
              <OfflineServerSection
                styles={styles}
                onConnect={handleConnect}
              />
              {status.alert && (
                <div
                  style={{
                    ...styles.alert,
                    ...(status.alert.type === 'error' ? styles.errorAlert : styles.warningAlert),
                    color: status.color // Use the status color instead of CSS variable
                  }}
                >
                  <span>{status.alert.message}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <EngineList styles={styles} groupedEngines={groupedEngines} t={t} />
              {!hasGateway && (
                <>
                  {status.alert && (
                    <div
                      style={{
                        ...styles.alert,
                        ...(status.alert.type === 'error' ? styles.errorAlert : styles.warningAlert),
                        color: status.color // Use the status color instead of CSS variable
                      }}
                    >
                      <span>{status.alert.message}</span>
                    </div>
                  )}
                  <div style={{
                    ...styles.divider,
                    marginTop: '16px',
                  }}>
                    <div style={styles.dividerLine} />
                    <span style={styles.dividerText}>or upgrade to</span>
                  </div>
                  <OfflineServerSection
                    styles={styles}
                    onConnect={handleConnect}
                  />
                </>
              )}
            </>
          )}
        </ExpandedView>
      )}

      {/* First-run: no engine at all and not mid-QR-scan. Render an amber
          "attention" pill that reads as an action (Connect WhatsApp) instead of
          the passive "Not Connected" status label. Once any engine exists (or a
          QR scan is in progress) we fall back to the calm status pill. */}
      {engines.length === 0 && !isInQRMode ? (
        <HeaderHint
          tone="warn"
          body={t('status.hint.engine.connect', 'Connect WhatsApp so your scheduled messages can be sent.')}
          footer={manageFooter}
        >
        <button
          data-connected-engines-button={instanceId}
          data-action-button="true"
          className={`${hs.chip} ${hs.chipButton} ${hs.toneWarn}`}
          onClick={handleStatusClick}
          onContextMenu={handleRightClick}
        >
          <PlugZap size={15} className={hs.icon} />
          <span className={hs.label}>
            {t('status.connectWhatsApp')}
          </span>
        </button>
        </HeaderHint>
      ) : (
        <HeaderHint
          tone={toneOf(status.color)}
          body={getStatusHint(status.text) ?? getTranslatedStatus(status.text)}
          footer={manageFooter}
        >
        <button
          data-connected-engines-button={instanceId}
          data-action-button="true"
          className={`${hs.chip} ${hs.chipButton} ${statusToneClass(status.color)}`}
          onClick={handleStatusClick}
          onContextMenu={handleRightClick}
        >
          <div className={hs.dot} />
          <span className={hs.label}>
            {getTranslatedStatus(status.text)}
          </span>
          {isInQRMode && (
            <QrCode size={15} className={hs.icon} />
          )}
          {!isNotifierConnected && (
            <WifiOff size={15} className={hs.iconBad} />
          )}
          {/* {isExpanded ? (
            <ChevronDown size={16} style={styles.chevronColor} />
          ) : (
            <ChevronUp size={16} style={styles.chevronColor} />
          )} */}
        </button>
        </HeaderHint>
      )}
    </div>
  );
};

export default ConnectedEngines; 